"use client";

import { bisector } from "d3-array";
import { useCallback, useContext, useState } from "react";
import { ChartContext } from "../../../context/ChartContext";
import { type BaseDataPoint, defaultMargin } from "../../../utils/charts";
import { getResolvedColor } from "../../../utils/colors";

/**
 * Event marker data point
 */
export interface EventMarker {
  /**
   * Event timestamp
   */
  timestamp: Date;
  /**
   * Event label/name
   */
  label: string;
  /**
   * Optional event type for styling
   */
  type?: "command" | "alert" | "info" | "success" | "warning" | "error";
  /**
   * Optional metadata to display in tooltip
   */
  metadata?: Record<string, string | number>;
}

export interface ChartEventMarkersProps {
  /**
   * Array of event markers to display
   */
  events: EventMarker[];
  /**
   * Marker color (defaults to theme-aware color)
   */
  color?: string;
  /**
   * Whether to show labels above markers
   * @default false
   */
  showLabels?: boolean;
}

/**
 * Get resolved color for event type
 * Uses getResolvedColor to ensure CSS variables work in SVG context
 */
function getEventColor(type: EventMarker["type"]): string {
  switch (type) {
    case "command":
      return getResolvedColor("--color-action-primary", "#3b82f6");
    case "success":
      return getResolvedColor("--color-feedback-success", "#10b981");
    case "warning":
      return getResolvedColor("--color-feedback-warning", "#f59e0b");
    case "error":
      return getResolvedColor("--color-feedback-error", "#ef4444");
    case "alert":
      return getResolvedColor("--color-feedback-warning", "#f59e0b");
    case "info":
    default:
      return getResolvedColor("--color-action-secondary", "#6b7280");
  }
}

/**
 * ChartEventMarkers
 *
 * Renders vertical lines with markers for events (e.g., commands) on a chart.
 * Must be used as a child of ChartContainer to access chart context.
 *
 * @example
 * ```tsx
 * <ChartContainer>
 *   <AreaSeries data={data} label="Power" />
 *   <ChartEventMarkers
 *     events={[
 *       { timestamp: new Date('2024-01-01T10:00:00Z'), label: 'Command sent', type: 'command' },
 *       { timestamp: new Date('2024-01-01T12:00:00Z'), label: 'Alert triggered', type: 'alert' }
 *     ]}
 *   />
 * </ChartContainer>
 * ```
 */
export function ChartEventMarkers({ events, color, showLabels = false }: ChartEventMarkersProps) {
  const context = useContext(ChartContext);
  const [hoveredEvent, setHoveredEvent] = useState<number | null>(null);

  if (!context) {
    console.warn("ChartEventMarkers must be used within a ChartContainer");
    return null;
  }

  const { xScale, yScale, height, tooltip, datasets, seriesMetadata, seriesTypes, categoryColors } = context;

  // Filter events that are within the visible domain
  const visibleEvents = events.filter((event) => {
    const xValue = xScale(event.timestamp);
    return xValue >= defaultMargin.left && xValue <= xScale.range()[1];
  });

  const handleMouseEnter = useCallback(
    (event: EventMarker, index: number) => {
      setHoveredEvent(index);
      const x = xScale(event.timestamp);
      const shouldShowLeft =
        x > (xScale.range()[1] - defaultMargin.right - defaultMargin.left) / 2 + defaultMargin.left;

      // Find nearest data points from all series at this timestamp
      const seriesData: Array<{
        label: string;
        value: number;
        color: string;
        type?: "area" | "line" | "bar" | "event";
        category?: string;
        categoryColor?: string;
      }> = [];
      if (datasets && seriesMetadata) {
        datasets.forEach((seriesDataset, datasetIndex) => {
          if (seriesDataset.length === 0) return;

          // Use bisector to find the nearest data point
          const bisectDate = bisector<BaseDataPoint, Date>((d) => d.xValue).left;
          const idx = bisectDate(seriesDataset, event.timestamp);

          // Get the closest point (either before or after the event timestamp)
          const d0 = seriesDataset[idx - 1];
          const d1 = seriesDataset[idx];

          let point;
          if (!d0) {
            point = d1;
          } else if (!d1) {
            point = d0;
          } else {
            // Choose the closer point
            const timeDiff0 = Math.abs(event.timestamp.getTime() - d0.xValue.getTime());
            const timeDiff1 = Math.abs(event.timestamp.getTime() - d1.xValue.getTime());
            point = timeDiff0 < timeDiff1 ? d0 : d1;
          }

          if (point && seriesMetadata[datasetIndex]) {
            seriesData.push({
              label: seriesMetadata[datasetIndex].label,
              value: point.yValue,
              color: seriesMetadata[datasetIndex].color,
              type: seriesTypes?.[datasetIndex] || "line",
              ...(point.category && {
                category: point.category,
                categoryColor: categoryColors?.[point.category],
              }),
            });
          }
        });
      }

      // Add event information to the end of the series array
      seriesData.push({
        label: event.label,
        value: 0, // Events don't have a y-value
        color: color || getEventColor(event.type),
        type: "event" as const,
      });

      // Show tooltip with both series data and event information
      tooltip.show({
        xValue: event.timestamp,
        series: seriesData,
        x,
        y: seriesData.length > 1 && typeof seriesData[0].value === "number" ? yScale(seriesData[0].value) : height / 2,
        position: shouldShowLeft ? "left" : "right",
        isEvent: true,
        eventMetadata: event.metadata,
      });
    },
    [xScale, yScale, height, tooltip, color, datasets, seriesMetadata, seriesTypes, categoryColors]
  );

  const handleMouseLeave = useCallback(() => {
    setHoveredEvent(null);
    tooltip.hide();
  }, [tooltip]);

  if (visibleEvents.length === 0) {
    return null;
  }

  return (
    <g className="chart-event-markers" data-testid="chart-event-markers">
      {visibleEvents.map((event, index) => {
        const x = xScale(event.timestamp);
        const eventColor = color || getEventColor(event.type);
        const isHovered = hoveredEvent === index;

        return (
          <g key={`${event.timestamp.getTime()}-${index}`}>
            {/* Vertical line */}
            <line
              x1={x}
              y1={defaultMargin.top}
              x2={x}
              y2={height - defaultMargin.bottom}
              stroke={eventColor}
              strokeWidth={isHovered ? 3 : 2}
              strokeDasharray="4 4"
              strokeOpacity={isHovered ? 0.9 : 0.6}
              pointerEvents="none"
            />

            {/* Top marker circle */}
            <circle
              cx={x}
              cy={defaultMargin.top}
              r={isHovered ? 5 : 4}
              fill={eventColor}
              stroke="var(--color-background-surface)"
              strokeWidth={2}
              pointerEvents="none"
            />

            {/* Bottom marker circle */}
            <circle
              cx={x}
              cy={height - defaultMargin.bottom}
              r={isHovered ? 5 : 4}
              fill={eventColor}
              stroke="var(--color-background-surface)"
              strokeWidth={2}
              pointerEvents="none"
            />

            {/* Label (if enabled) */}
            {showLabels && (
              <text
                x={x}
                y={defaultMargin.top - 8}
                textAnchor="middle"
                fill="var(--color-text-caption)"
                fontSize={11}
                fontWeight={500}
                pointerEvents="none"
              >
                {event.label}
              </text>
            )}

            {/* Interactive area for hover */}
            <rect
              x={x - 10}
              y={defaultMargin.top}
              width={20}
              height={height - defaultMargin.top - defaultMargin.bottom}
              fill="transparent"
              style={{ cursor: "pointer" }}
              onMouseEnter={() => handleMouseEnter(event, index)}
              onMouseLeave={handleMouseLeave}
            />
          </g>
        );
      })}
    </g>
  );
}
